import { powerSyncDb } from "@/lib/powersync/db"
import { psUpdate } from "@/lib/powersync/crud"
import type { PhotoInput } from "@/lib/queryClient/PartialSuccessError"
import {
  ASSOCIATION_TABLES,
  ENTITY_COLUMNS,
  deletePhotoFn,
} from "./useDeletePhoto"
import {
  persistPhotoInputsSequentially,
  type PhotoEntityType,
} from "./useUploadPhoto"

/**
 * Persist a new record's staged photos, reconciling with any earlier attempt
 * at the same create.
 *
 * A create that failed on one photo keeps its form open for retry, but the
 * photos that did save already have rows attached to the (stable) entity id.
 * On the retry:
 * - photos already attached are not rewritten, since saveFile would re-queue
 *   the full-resolution upload; only their caption is brought up to date.
 * - photos attached by the earlier attempt but no longer staged are deleted,
 *   since the user removed them from the form before trying again.
 *
 * Only for entities being created: every photo attached to the entity is
 * assumed to have come from this form.
 */
export async function persistStagedPhotosForNewEntity({
  photos,
  entityId,
  entityType,
  onError,
}: {
  photos: PhotoInput[]
  entityId: string
  entityType: PhotoEntityType
  onError?: (photo: PhotoInput, error: unknown) => void
}): Promise<PhotoInput[]> {
  const entityColumn = ENTITY_COLUMNS[entityType]
  const attached = await powerSyncDb.getAll<{
    id: string
    caption: string | null
  }>(
    `SELECT p.id, p.caption FROM photos p
     WHERE p.id IN (${ASSOCIATION_TABLES[entityType]
       .map(
         (table) => `SELECT photo_id FROM ${table} WHERE ${entityColumn} = ?`,
       )
       .join(" UNION ")})`,
    ASSOCIATION_TABLES[entityType].map(() => entityId),
  )

  const stagedIds = new Set(photos.map((photo) => photo.id))
  for (const photo of attached) {
    if (stagedIds.has(photo.id)) continue
    await deletePhotoFn({
      id: photo.id,
      entity_id: entityId,
      entity_type: entityType,
    })
  }

  const attachedCaptions = new Map(
    attached.map((photo) => [photo.id, photo.caption]),
  )
  const unsaved: PhotoInput[] = []
  for (const photo of photos) {
    if (!attachedCaptions.has(photo.id)) {
      unsaved.push(photo)
      continue
    }
    const caption = photo.caption ?? null
    if (attachedCaptions.get(photo.id) !== caption) {
      await psUpdate("photos", photo.id, {
        caption,
        updated_at: new Date().toISOString(),
      })
    }
  }

  return persistPhotoInputsSequentially({
    photos: unsaved,
    entityId,
    entityType,
    onError,
  })
}
